<template>
  <form
    @submit.prevent="submitBuyerForm"
    class="add-buyer-bank-form"
    :disabled="loading"
  >
    <b-row>
      <b-col>
        <h4 class="form-title my-2">{{ $t('coverLetter.addBuyerLabel') }}</h4>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('bankName'),
            'is-empty': formFields.bankName === ''
          }"
        >
          <label>
            <span class="input-label">{{
              $t('coverLetter.inputLabels.bankName')
            }}</span>
          </label>
          <b-form-input
            v-validate="'required|min:3|max:70'"
            name="bankName"
            type="text"
            :placeholder="$t('coverLetter.placeholders.bankName')"
            v-model="formFields.bankName"
            data-vv-as="Bank Name"
          ></b-form-input>
          <span class="error">
            {{ errors.first('bankName') }}
          </span>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('bankAddress'),
            'is-empty': formFields.bankAddress === ''
          }"
        >
          <label>
            <span class="input-label">{{
              $t('coverLetter.inputLabels.bankAddress')
            }}</span>
          </label>
          <b-form-input
            v-validate="'required|min:15|max:200'"
            name="bankAddress"
            type="text"
            :placeholder="$t('coverLetter.placeholders.bankAddress')"
            v-model="formFields.bankAddress"
            data-vv-as="Buyer Bank Address"
          ></b-form-input>
          <span class="error">
            {{ errors.first('bankAddress') }}
          </span>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('swift'),
            'is-empty': formFields.swift === ''
          }"
        >
          <label>
            <span class="input-label">
              {{ $t('coverLetter.inputLabels.swift') }}
            </span>
          </label>
          <b-form-input
            v-uppercase
            v-validate="{
              required: true,
              min: 8,
              max: 11,
              regex: /^[a-zA-Z]{6}[a-zA-Z0-9]{2}(?:[a-zA-Z0-9]{3})?$/
            }"
            name="swift"
            type="text"
            :placeholder="$t('coverLetter.placeholders.swift')"
            v-model="formFields.swift"
            data-vv-as="Swift"
          ></b-form-input>
          <span class="error">
            {{ errors.first('swift') }}
          </span>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('post'),
            'is-empty': formFields.post === ''
          }"
        >
          <label>
            <span class="input-label">
              {{ $t('coverLetter.inputLabels.pinCode') }}
            </span>
          </label>
          <b-form-input
            v-uppercase
            v-validate="'required|alpha_num|min:3|max:15'"
            name="post"
            type="text"
            :placeholder="$t('coverLetter.placeholders.pinCode')"
            v-model="formFields.post"
            data-vv-as="PIN Code"
          ></b-form-input>
          <span class="error">
            {{ errors.first('post') }}
          </span>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('contactName'),
            'is-empty': formFields.contactName === ''
          }"
        >
          <label>
            <span class="input-label">
              {{ $t('coverLetter.inputLabels.contactName') }}
              <span class="optional">
                ({{ $t('coverLetter.inputLabels.optional') }})
              </span>
            </span>
          </label>
          <b-form-input
            v-validate="'min:3|max:70'"
            name="contactName"
            type="text"
            :placeholder="$t('coverLetter.placeholders.contactName')"
            v-model="formFields.contactName"
            data-vv-as=" "
          ></b-form-input>
          <span class="error">
            {{ errors.first('contactName') }}
          </span>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group
          class="dc-group"
          :class="{
            'is-invalid': errors.has('contactNumber'),
            'is-empty': formFields.contactNumber === ''
          }"
        >
          <label>
            <span class="input-label">
              {{ $t('coverLetter.inputLabels.contactNumber') }}
              <span class="optional">
                ({{ $t('coverLetter.inputLabels.optional') }})
              </span>
            </span>
          </label>
          <b-form-input
            v-validate="'numeric'"
            name="contactNumber"
            type="text"
            :placeholder="$t('coverLetter.placeholders.contactNumber')"
            v-model="formFields.contactNumber"
            data-vv-as=" "
          ></b-form-input>
          <span class="error">
            {{ errors.first('contactNumber') }}
          </span>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- edit mode -->
    <div class="actions-container d-flex">
      <b-button
        :disabled="loading"
        type="submit"
        variant="primary"
        class="mr-3"
      >
        <loader v-if="loading" color="white" :height="5" />
        <template v-else>
          {{
            newBank
              ? $t('coverLetter.inputLabels.addBank')
              : $t('coverLetter.inputLabels.save')
          }}
        </template>
      </b-button>
      <b-button
        :disabled="loading"
        @click="$emit('close')"
        variant="primary"
        class="primary-outlined"
      >
        {{ $t('coverLetter.inputLabels.cancel') }}
      </b-button>
    </div>
  </form>
</template>

<script>
import Loader from '@/components/common/Loader'

export default {
  props: {
    newBank: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      formFields: {
        bankName: '',
        bankAddress: '',
        swift: '',
        post: '',
        contactName: '',
        contactNumber: ''
      }
    }
  },
  components: {
    Loader
  },
  methods: {
    submitBuyerForm() {
      return this.$validator.validate().then((result) => {
        if (result) {
          return this.$emit('save-data', this.formFields)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.add-buyer-bank-form {
  .form-title {
    color: $noble-blue-500;
    font-size: 1.25rem;
    font-weight: 700;
  }
  .actions-container {
    .primary-outlined {
      background-color: transparent;
      border: 0.0625rem solid $primary;
      color: $primary;
    }
  }

  .input-label {
    color: $dc-input-color !important;
  }

  .optional {
    color: $dc-label-color !important;
  }
}
</style>
